import React, { useState, useEffect } from 'react';
import {
  Video,
  Square,
  Pause,
  Play,
  Settings,
  X,
  Circle,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';

interface RecordingToolProps {
  onStartRecording: () => void;
  onStopRecording: () => Promise<Blob>;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  isRecording: boolean;
  isPaused: boolean;
}

export const RecordingTool: React.FC<RecordingToolProps> = ({
  onStartRecording,
  onStopRecording,
  onPauseRecording,
  onResumeRecording,
  isRecording,
  isPaused,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [duration, setDuration] = useState(0);

  const { recordingSettings, updateRecordingSettings } = useLivePreviewStore();

  // Duration timer
  useEffect(() => {
    if (!isRecording || isPaused) return;

    const interval = setInterval(() => {
      setDuration((d) => d + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isRecording, isPaused]);

  // Reset duration when stopped
  useEffect(() => {
    if (!isRecording) {
      setDuration(0);
    }
  }, [isRecording]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      await onStopRecording();
    } else {
      onStartRecording();
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        {/* Record/Stop button */}
        <button
          onClick={handleToggleRecording}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded text-sm',
            isRecording
              ? 'bg-red-600 hover:bg-red-500 text-white'
              : 'bg-zinc-700 hover:bg-zinc-600'
          )}
          title={isRecording ? 'Stop Recording' : 'Start Recording'}
        >
          {isRecording ? (
            <>
              <Square size={16} />
              <span className="font-mono">{formatDuration(duration)}</span>
            </>
          ) : (
            <>
              <Video size={16} />
              <span>Record</span>
            </>
          )}
        </button>

        {/* Pause/Resume button (when recording) */}
        {isRecording && (
          <button
            onClick={isPaused ? onResumeRecording : onPauseRecording}
            className="p-1.5 rounded hover:bg-zinc-700"
            title={isPaused ? 'Resume' : 'Pause'}
          >
            {isPaused ? <Play size={16} /> : <Pause size={16} />}
          </button>
        )}

        {/* Recording indicator */}
        {isRecording && !isPaused && (
          <Circle size={12} className="text-red-500 animate-pulse fill-current" />
        )}

        {/* Settings button */}
        {!isRecording && (
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={cn(
              'p-1.5 rounded hover:bg-zinc-700',
              showSettings && 'bg-zinc-700'
            )}
          >
            <Settings size={16} />
          </button>
        )}
      </div>

      {/* Settings dropdown */}
      {showSettings && !isRecording && (
        <div className="absolute top-full right-0 mt-1 w-64 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Recording Settings</span>
            <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-zinc-700 rounded">
              <X size={14} />
            </button>
          </div>

          {/* Format */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Format</label>
            <div className="flex gap-1">
              {(['mp4', 'webm', 'gif'] as const).map((format) => (
                <button
                  key={format}
                  onClick={() => updateRecordingSettings({ format })}
                  className={cn(
                    'flex-1 px-2 py-1 rounded text-xs uppercase',
                    recordingSettings.format === format
                      ? 'bg-blue-600'
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  )}
                >
                  {format}
                </button>
              ))}
            </div>
          </div>

          {/* Quality */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Quality</label>
            <div className="flex gap-1">
              {(['low', 'medium', 'high'] as const).map((quality) => (
                <button
                  key={quality}
                  onClick={() => updateRecordingSettings({ quality })}
                  className={cn(
                    'flex-1 px-2 py-1 rounded text-xs capitalize',
                    recordingSettings.quality === quality
                      ? 'bg-blue-600'
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  )}
                >
                  {quality}
                </button>
              ))}
            </div>
          </div>

          {/* FPS */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">FPS</label>
            <div className="flex gap-1">
              {[15, 30, 60].map((fps) => (
                <button
                  key={fps}
                  onClick={() => updateRecordingSettings({ fps })}
                  className={cn(
                    'flex-1 px-2 py-1 rounded text-xs',
                    recordingSettings.fps === fps
                      ? 'bg-blue-600'
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  )}
                >
                  {fps}
                </button>
              ))}
            </div>
          </div>

          {/* Include Audio */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={recordingSettings.includeAudio}
              onChange={(e) =>
                updateRecordingSettings({ includeAudio: e.target.checked })
              }
              className="w-4 h-4 rounded bg-zinc-700"
            />
            <span className="text-sm">Include Audio</span>
          </label>

          {/* Filename */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Filename prefix</label>
            <input
              type="text"
              value={recordingSettings.filename}
              onChange={(e) =>
                updateRecordingSettings({ filename: e.target.value })
              }
              className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};
